import { useEffect, useState, useCallback } from 'react';
import { useSyncSystem } from '@/lib/sync/sync-system-context';
import { keepStateForView } from './filter-presence-by-view';

const IDLE_MS = 20_000;
const GONE_MS = 60_000;

export interface RemoteUser {
  id: string;
  name: string;
  color: string;
  avatar?: string;
  isIdle: boolean;
}

/**
 * Remote presence for an entity's awareness.
 *
 * `viewId` (optional) scopes presence to a single sub-view of a multi-surface
 * entity (e.g. one derived tab). When set, only peers who published the SAME
 * `viewId` are returned (fail-closed — see `keepStateForView`). Omit it for a
 * single-surface entity to get every present peer (the default / legacy
 * behavior). Each surface publishes its own `viewId` via `usePublishAwarenessView`.
 */
export function useAwareness(entityId: string, viewId?: string): { remoteUsers: RemoteUser[] } {
  const { registry } = useSyncSystem();
  const [remoteUsers, setRemoteUsers] = useState<RemoteUser[]>([]);

  const refresh = useCallback(() => {
    const awareness = registry.getAwareness(entityId);
    if (!awareness) {
      setRemoteUsers([]);
      return;
    }

    const states = awareness.getStates();
    const localId = awareness.clientID;
    const seen = new Set<string>();
    const users: RemoteUser[] = [];
    const now = Date.now();

    states.forEach((state, clientId) => {
      if (clientId === localId) return;
      if (!keepStateForView(state as { viewId?: string }, viewId)) return;
      const lastUpdated = (awareness.meta as Map<number, { lastUpdated: number }>).get(clientId)?.lastUpdated ?? now;
      const age = now - lastUpdated;
      if (age > GONE_MS) return;
      const user = state.user;
      const name = user?.name || state.name;
      const color = user?.color || state.color;
      if (!name) return;
      const id = user?.id || `client-${clientId}`;
      // Deduplicate by user ID (same user with multiple tabs)
      if (seen.has(id)) return;
      seen.add(id);
      users.push({
        id,
        name,
        color: color || 'var(--accent)',
        avatar: user?.avatar || undefined,
        isIdle: age > IDLE_MS,
      });
    });

    setRemoteUsers(users);
  }, [registry, entityId, viewId]);

  useEffect(() => {
    const awareness = registry.getAwareness(entityId);
    if (!awareness) return;

    // Initial read
    refresh();

    const handler = () => refresh();
    awareness.on('update', handler);
    const interval = setInterval(refresh, 5_000);
    return () => {
      clearInterval(interval);
      try { awareness.off('update', handler); } catch { /* awareness already destroyed */ }
    };
  }, [registry, entityId, refresh]);

  return { remoteUsers };
}
